// 🌿 Weed Characteristics Database for Herbicide Timing Calculator
// Comprehensive data for pre-emergent and post-emergent herbicide applications

import { resolveWeedAlias } from '@/data/weed-registry'

export interface WeedCharacteristics {
  name: string
  category: 'pre-emergent' | 'post-emergent' | 'both'
  germinationTemp: {
    min: number // °F
    optimal: number // °F
  }
  applicationTiming: {
    preEmergent: {
      soilTempTarget: number // °F
      timingWindow: string
      criticalDates: string
      urgencyDays: number // Days before optimal window closes
    }
    postEmergent: {
      bestTime: string
      growthStage: string
      weatherConditions: string[]
      seasonalTiming: string[]
    }
  }
  commonRegions: string[]
  difficulty: 'easy' | 'moderate' | 'difficult'
  notes: string[]
  controlTips: string[]
}

export const WEED_CHARACTERISTICS: Record<string, WeedCharacteristics> = {
  'Crabgrass': {
    name: 'Crabgrass',
    category: 'both',
    germinationTemp: {
      min: 55,
      optimal: 65
    },
    applicationTiming: {
      preEmergent: {
        soilTempTarget: 55,
        timingWindow: 'Late winter to early spring',
        criticalDates: 'Before soil reaches 55°F for 3 consecutive days',
        urgencyDays: 14
      },
      postEmergent: {
        bestTime: 'Early summer when young',
        growthStage: 'Before tillering (4-6 leaf stage)',
        weatherConditions: ['Apply when weeds are dry', 'No rain for 24 hours', 'Temps below 85°F'],
        seasonalTiming: ['Late spring', 'Early summer']
      }
    },
    commonRegions: ['All regions'],
    difficulty: 'moderate',
    notes: [
      'Most effective as pre-emergent',
      'Can be controlled post-emergent when young',
      'Produces thousands of seeds per plant',
      'Thrives in compacted soil'
    ],
    controlTips: [
      'Apply pre-emergent before soil reaches 55°F',
      'Maintain thick, healthy grass to prevent establishment',
      'Avoid overwatering in summer',
      'Consider overseeding in fall to improve density'
    ]
  },

  'Dandelions': {
    name: 'Dandelions',
    category: 'post-emergent',
    germinationTemp: {
      min: 40,
      optimal: 55
    },
    applicationTiming: {
      preEmergent: {
        soilTempTarget: 45,
        timingWindow: 'Fall or early spring',
        criticalDates: 'Before seeds germinate in spring or fall',
        urgencyDays: 21
      },
      postEmergent: {
        bestTime: 'Fall or early spring',
        growthStage: 'Actively growing, not flowering',
        weatherConditions: ['Apply when weeds are dry', 'No rain for 24 hours', 'Temps 60-80°F'],
        seasonalTiming: ['Fall (September-October)', 'Early spring (March-April)']
      }
    },
    commonRegions: ['All regions'],
    difficulty: 'easy',
    notes: [
      'Deep taproot makes mechanical removal difficult',
      'Produces seeds that spread easily',
      'Most effective control is post-emergent in fall',
      'Can be controlled with selective herbicides'
    ],
    controlTips: [
      'Apply post-emergent in fall when dandelions are actively growing',
      'Use selective herbicides safe for your grass type',
      'Remove seed heads before they mature',
      'Improve lawn density to prevent establishment'
    ]
  },

  'White Clover': {
    name: 'White Clover',
    category: 'post-emergent',
    germinationTemp: {
      min: 45,
      optimal: 60
    },
    applicationTiming: {
      preEmergent: {
        soilTempTarget: 50,
        timingWindow: 'Early spring or fall',
        criticalDates: 'Before seeds germinate',
        urgencyDays: 14
      },
      postEmergent: {
        bestTime: 'Spring or fall',
        growthStage: 'Actively growing',
        weatherConditions: ['Apply when weeds are dry', 'No rain for 24 hours', 'Temps 60-75°F'],
        seasonalTiming: ['Spring (April-May)', 'Fall (September-October)']
      }
    },
    commonRegions: ['All regions'],
    difficulty: 'moderate',
    notes: [
      'Fixes nitrogen, can actually benefit soil',
      'Indicates low nitrogen in soil',
      'Can be controlled with selective herbicides',
      'Often appears in thin or stressed lawns'
    ],
    controlTips: [
      'Increase nitrogen fertilization to outcompete clover',
      'Use selective herbicides in spring or fall',
      'Improve lawn density through proper care',
      'Consider that clover can be beneficial to soil health'
    ]
  },

  'Foxtail (Setaria)': {
    name: 'Foxtail (Setaria)',
    category: 'both',
    germinationTemp: {
      min: 60,
      optimal: 75
    },
    applicationTiming: {
      preEmergent: {
        soilTempTarget: 60,
        timingWindow: 'Late spring',
        criticalDates: 'Before soil reaches 60°F',
        urgencyDays: 10
      },
      postEmergent: {
        bestTime: 'Early summer',
        growthStage: 'Young plants (2-4 inches)',
        weatherConditions: ['Apply when weeds are dry', 'No rain for 24 hours', 'Temps below 90°F'],
        seasonalTiming: ['Late spring', 'Early summer']
      }
    },
    commonRegions: ['Central', 'South Central', 'Great Lakes', 'Upper Midwest'],
    difficulty: 'moderate',
    notes: [
      'Warm-season annual grass',
      'Produces distinctive seed heads',
      'Prefers compacted soil',
      'Can be controlled with selective herbicides'
    ],
    controlTips: [
      'Apply pre-emergent in late spring',
      'Control when young for best results',
      'Improve soil aeration to prevent establishment',
      'Use selective herbicides safe for your grass type'
    ]
  },

  'Goosegrass': {
    name: 'Goosegrass',
    category: 'both',
    germinationTemp: {
      min: 60,
      optimal: 80
    },
    applicationTiming: {
      preEmergent: {
        soilTempTarget: 60,
        timingWindow: 'Late spring to early summer',
        criticalDates: 'Before soil reaches 60°F (germinates 2-3 weeks after crabgrass)',
        urgencyDays: 10
      },
      postEmergent: {
        bestTime: 'Early summer',
        growthStage: 'Young plants (2-3 inches)',
        weatherConditions: ['Apply when weeds are dry', 'No rain for 24 hours', 'Temps below 85°F'],
        seasonalTiming: ['Late spring', 'Early summer']
      }
    },
    commonRegions: ['Southeast', 'South Central', 'Mid-Atlantic', 'Central'],
    difficulty: 'difficult',
    notes: [
      'Very tough, low-growing annual grass',
      'Resistant to many herbicides',
      'Prefers compacted, high-traffic areas',
      'Requires specific herbicide products'
    ],
    controlTips: [
      'Apply pre-emergent before soil reaches 65°F',
      'Use products specifically labeled for goosegrass',
      'Improve soil aeration and reduce compaction',
      'May require multiple applications for complete control'
    ]
  },

  'Broadleaf Plantain': {
    name: 'Broadleaf Plantain',
    category: 'post-emergent',
    germinationTemp: {
      min: 45,
      optimal: 60
    },
    applicationTiming: {
      preEmergent: {
        soilTempTarget: 50,
        timingWindow: 'Early spring or fall',
        criticalDates: 'Before seeds germinate',
        urgencyDays: 14
      },
      postEmergent: {
        bestTime: 'Spring or fall',
        growthStage: 'Actively growing',
        weatherConditions: ['Apply when weeds are dry', 'No rain for 24 hours', 'Temps 60-80°F'],
        seasonalTiming: ['Spring (April-May)', 'Fall (September-October)']
      }
    },
    commonRegions: ['All regions'],
    difficulty: 'easy',
    notes: [
      'Indicates compacted soil conditions',
      'Has medicinal properties but unwanted in lawns',
      'Can be controlled with selective herbicides',
      'Often found in high-traffic areas'
    ],
    controlTips: [
      'Improve soil aeration to address underlying cause',
      'Use selective herbicides in spring or fall',
      'Remove manually if few plants present',
      'Improve lawn density to prevent establishment'
    ]
  },

  'Nutsedge (Yellow)': {
    name: 'Nutsedge (Yellow)',
    category: 'post-emergent',
    germinationTemp: {
      min: 60,
      optimal: 75
    },
    applicationTiming: {
      preEmergent: {
        soilTempTarget: 60,
        timingWindow: 'Pre-emergent herbicides are generally ineffective against nutsedge',
        criticalDates: 'N/A. Nutsedge regrows from underground tubers, not seeds.',
        urgencyDays: 0
      },
      postEmergent: {
        bestTime: 'Late spring to early summer when actively growing',
        growthStage: '3-8 leaf stage, before plants produce new tubers',
        weatherConditions: ['Apply when weeds are dry', 'No rain for 4 hours', 'Temps 60-90°F'],
        seasonalTiming: ['Late spring (May-June)', 'Early summer (June-July)']
      }
    },
    commonRegions: ['All regions'],
    difficulty: 'difficult',
    notes: [
      'Not a grass or broadleaf; it is a sedge (triangular stem, "sedges have edges")',
      'Spreads via underground tubers (nutlets) that persist for years in soil',
      'Grows faster than most turf grasses in warm weather',
      'Standard broadleaf herbicides like 2,4-D are ineffective; requires sedge-specific products'
    ],
    controlTips: [
      'Apply SedgeHammer (halosulfuron) or Certainty (sulfosulfuron) in late spring/early summer',
      'Treat when plants are young (3-8 leaves) before new tubers form in July',
      'Improve drainage; nutsedge thrives in wet, poorly drained areas',
      'Multiple applications over 2-3 years are usually needed to exhaust tuber bank'
    ]
  },

  'Creeping Charlie (Ground Ivy)': {
    name: 'Creeping Charlie (Ground Ivy)',
    category: 'post-emergent',
    germinationTemp: {
      min: 40,
      optimal: 55
    },
    applicationTiming: {
      preEmergent: {
        soilTempTarget: 50,
        timingWindow: 'Pre-emergent is ineffective; creeping charlie spreads primarily by stolons, not seeds',
        criticalDates: 'N/A. Focus on post-emergent control.',
        urgencyDays: 0
      },
      postEmergent: {
        bestTime: 'Fall (mid-September to November) when plant is moving sugars to roots',
        growthStage: 'Actively growing after first fall frost is ideal',
        weatherConditions: ['Apply when weeds are dry', 'No rain for 24 hours', 'Temps 50-70°F'],
        seasonalTiming: ['Fall (September-November)', 'Spring (April-May) as secondary timing']
      }
    },
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Upper Midwest', 'Central'],
    difficulty: 'difficult',
    notes: [
      'Aggressive perennial broadleaf that spreads by stolons forming dense mats',
      'Round scalloped leaves with distinctive minty odor when crushed',
      'Thrives in shady, moist areas where turf is thin',
      'Extremely difficult to control with a single application; creeping stems root at every node'
    ],
    controlTips: [
      'Apply triclopyr-based herbicide (e.g., Turflon Ester) in fall for best translocation to roots',
      'Combination of triclopyr + 2,4-D + dicamba is more effective than 2,4-D alone',
      'Expect 2-3 annual applications over multiple years for heavy infestations',
      'Improve turf density and reduce shade to prevent reinvasion'
    ]
  },

  'Annual Bluegrass (Poa annua)': {
    name: 'Annual Bluegrass (Poa annua)',
    category: 'both',
    germinationTemp: {
      min: 45,
      optimal: 55
    },
    applicationTiming: {
      preEmergent: {
        soilTempTarget: 70,
        timingWindow: 'Late summer to early fall (August-September)',
        criticalDates: 'Before soil drops below 70°F in fall (seeds germinate in fall, not spring)',
        urgencyDays: 14
      },
      postEmergent: {
        bestTime: 'Late fall or early spring when actively growing',
        growthStage: 'Young plants before seed head production',
        weatherConditions: ['Apply when weeds are dry', 'No rain for 24 hours', 'Temps 50-70°F'],
        seasonalTiming: ['Fall (October-November)', 'Early spring (March-April)']
      }
    },
    commonRegions: ['All regions'],
    difficulty: 'difficult',
    notes: [
      'Light-green annual (sometimes short-lived perennial) that produces seed heads even at low mowing heights',
      'Germinates in fall, not spring like crabgrass, so the timing is opposite of most grassy weed programs',
      'Each plant can produce hundreds of seeds, building a large seed bank in the soil',
      'Dies off in summer heat leaving bare patches, especially visible in bermudagrass and warm-season lawns'
    ],
    controlTips: [
      'Apply pre-emergent in early fall before soil drops below 70°F. Prodiamine and dithiopyr are both labeled for it.',
      'Ethofumesate (Prograss) provides both pre- and post-emergent control in cool-season turf',
      'In warm-season lawns, foramsulfuron (Revolver) can selectively remove Poa annua from bermudagrass',
      'Improve turf density through fall overseeding with desirable species to outcompete it'
    ]
  },

  'Spotted Spurge': {
    name: 'Spotted Spurge',
    category: 'both',
    germinationTemp: {
      min: 60,
      optimal: 75
    },
    applicationTiming: {
      preEmergent: {
        soilTempTarget: 60,
        timingWindow: 'Late spring',
        criticalDates: 'Before soil reaches 60°F consistently',
        urgencyDays: 10
      },
      postEmergent: {
        bestTime: 'Early summer when plants are small',
        growthStage: 'Young plants before they spread laterally',
        weatherConditions: ['Apply when weeds are dry', 'No rain for 24 hours', 'Temps below 90°F'],
        seasonalTiming: ['Late spring', 'Early summer']
      }
    },
    commonRegions: ['All regions'],
    difficulty: 'moderate',
    // Covers the prostrate-spurge complex, not Euphorbia maculata alone. The
    // species (maculata, prostrata, nutans) are separable only by capsule hair
    // pattern and seed ridging, which a phone photo cannot resolve, and UC IPM
    // states management of all the spurges is the same. Extension sources
    // themselves disagree on which common name maps to which species, so the
    // registry keeps 'prostrate spurge' as an alias rather than a second entry.
    notes: [
      'Low-growing summer annual that forms flat mats close to the ground',
      'Exudes milky white sap when stems are broken, which separates it from similar mat-forming plants',
      'Thrives in hot, dry conditions and compacted soil, so it shows up along driveways and thin turf edges',
      'Each plant can produce thousands of seeds, creating heavier infestations year after year'
    ],
    controlTips: [
      'Crabgrass pre-emergent also prevents spurge. The timing overlaps at 55-60°F soil temperature.',
      'Apply post-emergent 2,4-D plus dicamba, or carfentrazone (Quicksilver), to young plants',
      'Hand-pull small infestations before seed set and wear gloves, because the sap can irritate skin',
      'Aerate compacted areas and overseed to improve turf density where spurge colonizes'
    ]
  },

  'Henbit': {
    name: 'Henbit',
    category: 'both',
    germinationTemp: {
      min: 40,
      optimal: 50
    },
    applicationTiming: {
      preEmergent: {
        soilTempTarget: 65,
        timingWindow: 'Early to mid fall',
        criticalDates: 'Before soil drops below 65°F in fall (winter annual germinates in fall)',
        urgencyDays: 14
      },
      postEmergent: {
        bestTime: 'Late fall or early spring before flowering',
        growthStage: 'Actively growing rosette stage',
        weatherConditions: ['Apply when weeds are dry', 'No rain for 24 hours', 'Temps 45-65°F'],
        seasonalTiming: ['Fall (October-November)', 'Late winter/early spring (February-March)']
      }
    },
    commonRegions: ['All regions'],
    difficulty: 'easy',
    notes: [
      'Winter annual broadleaf with purple flowers in early spring',
      'Square stems and scalloped leaves in opposite pairs',
      'Germinates in fall, flowers and dies in late spring',
      'Often appears in thin or newly seeded lawns'
    ],
    controlTips: [
      'Apply fall pre-emergent before soil drops below 65°F',
      'Post-emergent with 2,4-D or triclopyr in fall or early spring',
      'Improve lawn density to prevent fall germination',
      'Plants die naturally in late spring but will have already set seed'
    ]
  },

  'Chickweed (Common)': {
    name: 'Chickweed (Common)',
    category: 'post-emergent',
    // SARE puts optimal germination at 54-68°F, with none above 86°F. The prior
    // 35°F floor was well below any published figure. commonRegions stays 'All
    // regions': Purdue and UMass both state common chickweed is distributed
    // across all regions of the US, so this is not a cool-season-only weed.
    germinationTemp: {
      min: 54,
      optimal: 68
    },
    applicationTiming: {
      preEmergent: {
        soilTempTarget: 65,
        timingWindow: 'Early fall before germination',
        criticalDates: 'Before soil drops below 65°F in fall',
        urgencyDays: 14
      },
      postEmergent: {
        bestTime: 'Late fall or early spring when actively growing',
        growthStage: 'Before flowering for best control',
        weatherConditions: ['Apply when weeds are dry', 'No rain for 24 hours', 'Temps 45-65°F'],
        seasonalTiming: ['Fall (October-November)', 'Early spring (March-April)']
      }
    },
    commonRegions: ['All regions'],
    difficulty: 'easy',
    notes: [
      'Cool-season annual/perennial with small white star-shaped flowers',
      'Forms dense, low-growing mats in moist shady areas',
      'One of the first weeds to appear in late winter/early spring',
      'Thrives in overwatered and heavily shaded lawns'
    ],
    controlTips: [
      'Apply broadleaf herbicide (2,4-D, dicamba, or triclopyr) in fall or early spring',
      'Reduce watering frequency and improve drainage',
      'Increase sunlight by pruning overhanging branches where possible',
      'Maintain proper mowing height to encourage turf density'
    ]
  },

  'Wild Violet': {
    name: 'Wild Violet',
    category: 'post-emergent',
    germinationTemp: {
      min: 40,
      optimal: 55
    },
    applicationTiming: {
      preEmergent: {
        soilTempTarget: 50,
        timingWindow: 'Pre-emergent is generally ineffective against established violets',
        criticalDates: 'N/A. Wild violets are perennials that spread by rhizomes and seed.',
        urgencyDays: 0
      },
      postEmergent: {
        bestTime: 'Fall (October-November) when plant is moving energy to roots',
        growthStage: 'Actively growing with full leaf development',
        weatherConditions: ['Apply when weeds are dry', 'No rain for 24 hours', 'Temps 50-75°F'],
        seasonalTiming: ['Fall (October-November)', 'Spring (April-May) as secondary timing']
      }
    },
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Upper Midwest', 'Central', 'Southeast'],
    difficulty: 'difficult',
    notes: [
      'Perennial broadleaf with heart-shaped leaves and purple flowers',
      'Waxy leaf coating makes herbicide absorption difficult',
      'Spreads aggressively by rhizomes and self-seeding',
      'Thrives in moist, shady areas but tolerates full sun'
    ],
    controlTips: [
      'Apply triclopyr-based herbicide in fall for best results (spring treatments are less effective)',
      'Add a surfactant/sticker to help herbicide penetrate waxy leaves',
      'Expect 2-3 annual fall applications for heavy infestations',
      'Repeated hand-pulling can control small patches if entire root system is removed'
    ]
  },
  // Quackgrass regrows from rhizomes, not seed, so category is 'post-emergent':
  // a pre-emergent barrier cannot stop buds that are already below the surface.
  // The preEmergent block follows the Wild Violet pattern for perennials
  // (timingWindow says the barrier does not apply, urgencyDays 0).
  'Quackgrass': {
    name: 'Quackgrass',
    category: 'post-emergent',
    germinationTemp: {
      min: 45,
      optimal: 60
    },
    applicationTiming: {
      preEmergent: {
        soilTempTarget: 50,
        timingWindow: 'Pre-emergent does not control quackgrass; an established patch regrows from underground rhizomes rather than from seed',
        criticalDates: 'N/A. A seed barrier cannot stop rhizome buds that are already below the surface.',
        urgencyDays: 0
      },
      postEmergent: {
        bestTime: 'Late summer to early fall, so a treated area can be reseeded while soil is still warm',
        growthStage: 'Actively growing shoots several inches tall, before the patch goes dormant',
        weatherConditions: ['Apply when the grass is dry', 'No rain for 24 hours', 'Temps 55-85°F'],
        seasonalTiming: ['Late summer (August)', 'Early fall (September)', 'Spring (April-May) as secondary timing']
      }
    },
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Upper Midwest', 'Central', 'Pacific'],
    difficulty: 'difficult',
    notes: [
      'Cool-season perennial grass that spreads by long white rhizomes with sharp pointed tips',
      'Every rhizome fragment can start a new plant, so pulling or rototilling an infested area multiplies it',
      'No selective herbicide is labeled to remove quackgrass from a cool-season lawn; the products that work kill the turf too',
      'Older leaves carry small claw-like auricles that clasp the stem at the collar, which separates it from crabgrass and most lawn grasses'
    ],
    controlTips: [
      'Dig small patches out with a garden fork, sifting the soil for every white rhizome piece, then bag the debris rather than composting it',
      'For larger patches, spot-treat with a non-selective glyphosate product, accept that the turf in that spot dies, and reseed after the label interval',
      'Do not till a treated area before seeding, because tilling brings rhizome fragments up to sprout; power rake or scarify for seed-to-soil contact instead',
      'Mow tall, feed on a regular schedule, and overseed thin areas so surfacing rhizome tips meet dense shade instead of open soil'
    ]
  },
  // Orchardgrass is a perennial bunchgrass with no selective post-emergent
  // either; the honesty lives in controlTips, which the guide page's derived
  // control-plan block renders.
  'Orchardgrass': {
    name: 'Orchardgrass',
    category: 'post-emergent',
    germinationTemp: {
      min: 50,
      optimal: 68
    },
    applicationTiming: {
      preEmergent: {
        soilTempTarget: 50,
        timingWindow: 'Pre-emergent does not control established orchardgrass clumps',
        criticalDates: 'N/A. Orchardgrass is a perennial bunchgrass that regrows from its crown; pre-emergent only stops new seed from starting.',
        urgencyDays: 0
      },
      postEmergent: {
        bestTime: 'Spring or early fall while clumps are actively growing',
        growthStage: 'Actively growing clumps, ideally with a week or two of regrowth after mowing',
        weatherConditions: ['Apply when foliage is dry', 'No rain for 24 hours', 'Temps 55-80°F'],
        seasonalTiming: ['Spring (April-May)', 'Early fall (September-October)']
      }
    },
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Upper Midwest', 'Central'],
    difficulty: 'difficult',
    notes: [
      'Cool-season perennial bunchgrass grown for pasture and hay, not for lawns',
      'Usually enters a lawn as a contaminant in discount seed mixes or straw mulch, or as seed from nearby pasture',
      'Forms discrete clumps from a crown with no rhizomes or stolons, so it does not run through the lawn',
      'Coarse gray-green blades and flattened stem bases separate it from the surrounding turf',
      'Outgrows lawn grasses between mowings, which is why clumps look shaggy two to three days after a cut'
    ],
    controlTips: [
      'No selective herbicide removes orchardgrass from a cool-season lawn; treat any product claiming otherwise as mislabeled',
      'Dig each clump out whole with an inch or two of surrounding soil, then refill and reseed the divot',
      'For heavy infestations, spot treat actively growing clumps with a nonselective glyphosate product, wait out the label interval, and reseed',
      'Buy certified seed and check the purity statement so repairs and overseeding stop reintroducing it',
      'Keep the lawn dense and overseed thin areas in early fall so incoming seed has no open soil to occupy'
    ]
  },
  // Creeping bentgrass spreads by stolons; mesotrione is the one selective
  // option and only on verified KBG, ryegrass, or tall fescue.
  'Creeping Bentgrass': {
    name: 'Creeping Bentgrass',
    category: 'post-emergent',
    germinationTemp: {
      min: 50,
      optimal: 60
    },
    applicationTiming: {
      preEmergent: {
        soilTempTarget: 55,
        timingWindow: 'Not a control route for established patches',
        criticalDates: 'Prevention only applies to new seed in a renovated or bare area. Pre-emergent does not remove stolons that are already growing.',
        urgencyDays: 21
      },
      postEmergent: {
        bestTime: 'Late summer into early fall while the mat is actively growing',
        growthStage: 'Actively growing green mat, well before dormancy',
        weatherConditions: ['Apply when the foliage is dry', 'No rain for 24 hours', 'Temps 60-85°F', 'Non-ionic surfactant required by the mesotrione label'],
        seasonalTiming: ['Late summer (August)', 'Early fall (September-October)']
      }
    },
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Upper Midwest', 'Pacific'],
    difficulty: 'difficult',
    notes: [
      'A cool-season perennial turfgrass used on golf greens, not an annual weed, so there is no yearly seed cycle to interrupt',
      'Spreads by above-ground stolons that root at the nodes and form dense, puffy, felt-like mats',
      'Mesotrione is the one selective option, and only on verified Kentucky bluegrass, perennial ryegrass, or tall fescue',
      'Selective control needs three or four applications through the season; a single application reliably fails',
      'Patches brown out early under summer heat and drought because the mat is shallow rooted'
    ],
    controlTips: [
      'Verify the host grass before any selective application; mesotrione is not for bermudagrass, zoysiagrass, or seashore paspalum, and it can thin fine fescue',
      'Follow the mesotrione label for rate, spacing, and the required non-ionic surfactant, and treat a margin past the visible patch edge',
      'For a few patches, strip the mat with a flat spade about an inch deep plus a few inches past the edge, then sod or seed the same day',
      'Water deeply and infrequently instead of a little every day, which is the condition bentgrass depends on',
      'Raise mowing height to three inches or more so the lawn is not being managed at putting-green height'
    ]
  }
}

// Identity lookup is intentionally exact after normalization. Product safety,
// guide routing, and evaluation labels must never turn a substring such as
// "grass" into Crabgrass.
export function findWeedByName(name: string): WeedCharacteristics | undefined {
  const canonical = resolveWeedAlias(name)
  return canonical ? WEED_CHARACTERISTICS[canonical.displayName] : undefined
}

// Helper function to get weeds by category
export function getWeedsByCategory(category: 'pre-emergent' | 'post-emergent' | 'both'): WeedCharacteristics[] {
  return Object.values(WEED_CHARACTERISTICS).filter(weed =>
    weed.category === category || weed.category === 'both'
  )
}

// Helper function to get weeds by region
export function getWeedsByRegion(region: string): WeedCharacteristics[] {
  return Object.values(WEED_CHARACTERISTICS).filter(weed =>
    weed.commonRegions.includes(region) || weed.commonRegions.includes('All regions')
  )
}

// Helper function to get weeds by difficulty
export function getWeedsByDifficulty(difficulty: 'easy' | 'moderate' | 'difficult'): WeedCharacteristics[] {
  return Object.values(WEED_CHARACTERISTICS).filter(weed => weed.difficulty === difficulty)
}
